/**
 * Water Material Behavior
 * 
 * Creates an animated water surface with reflections and wave animation.
 * Uses BabylonJS WaterMaterial from the materials library.
 * 
 * Blender Setup (material properties):
 *   hm_materialWater_enabled: true
 *   hm_materialWater_windForce: -5.0
 *   hm_materialWater_waveHeight: 0.1 (meaningful range: 0-0.2)
 *   hm_materialWater_bumpHeight: 0.1
 *   hm_materialWater_windDirectionX: 1.0
 *   hm_materialWater_windDirectionY: 1.0
 *   hm_materialWater_waterColorR: 0.1
 *   hm_materialWater_waterColorG: 0.1
 *   hm_materialWater_waterColorB: 0.6
 *   hm_materialWater_colorBlendFactor: 0.0
 *   hm_materialWater_waveLength: 0.1
 */

import {
  Texture,
  Color3,
  Vector2,
  VertexBuffer,
  type AbstractMesh,
  type Mesh,
  type Scene,
} from '@babylonjs/core';
import { WaterMaterial } from '@babylonjs/materials';
import { EntityType } from '../../types/EntityTypes';
import type { EntityBehavior } from '../../core/BehaviorRegistry';
import { logger } from '../../core/Logger';
import { registerMaterialBehavior, unregisterMaterialBehavior } from './MaterialBehaviorRegistry';

/**
 * Water material behavior parameters
 */
export interface WaterMaterialParams {
  windForce: number;
  waveHeight: number;
  bumpHeight: number;
  windDirectionX: number;
  windDirectionY: number;
  waterColorR: number;
  waterColorG: number;
  waterColorB: number;
  colorBlendFactor: number;
  waveLength: number;
}

// Store water materials for cleanup and live editing
const waterMaterialsByEntity = new Map<string, {
  material: WaterMaterial;
  mesh: AbstractMesh;
  params: WaterMaterialParams;
}>();

// Store observers separately to avoid circular JSON issues
const observersByEntity = new Map<string, any>();

/**
 * Ensure the mesh has tangent data required for bump mapping.
 * If tangents are missing, generate them from geometry.
 */
function ensureTangents(mesh: AbstractMesh): void {
  // Only Mesh instances have geometry manipulation methods
  if (!('getVerticesData' in mesh)) return;
  
  const m = mesh as Mesh;
  
  // Check if tangents already exist
  if (m.isVerticesDataPresent(VertexBuffer.TangentKind)) {
    logger.assets.debug(`water_material: Mesh ${mesh.name} already has tangents`);
    return;
  }
  
  // Get required vertex data
  const positions = m.getVerticesData(VertexBuffer.PositionKind);
  const normals = m.getVerticesData(VertexBuffer.NormalKind);
  const uvs = m.getVerticesData(VertexBuffer.UVKind);
  
  if (!positions || !normals || !uvs) {
    logger.assets.warn(`water_material: Cannot generate tangents for ${mesh.name} - missing position/normal/uv data`);
    return;
  }
  
  // Generate simple tangents for the water plane
  // For a typically horizontal water surface, tangent is (1, 0, 0, 1)
  const vertexCount = positions.length / 3;
  const tangents = new Float32Array(vertexCount * 4);
  
  for (let i = 0; i < vertexCount; i++) {
    const offset = i * 4;
    tangents[offset] = 1;     // x
    tangents[offset + 1] = 0; // y
    tangents[offset + 2] = 0; // z
    tangents[offset + 3] = 1; // handedness
  }
  
  m.setVerticesData(VertexBuffer.TangentKind, tangents, false);
  logger.assets.debug(`water_material: Generated tangents for ${mesh.name} (${vertexCount} vertices)`);
}

/**
 * Extract water material parameters from mesh material extras.
 * Reads hm_materialWater_* properties from the material.
 */
function extractWaterParams(mesh: AbstractMesh): WaterMaterialParams {
  // Check material extras first (correct location)
  const matExtras = (mesh.material as any)?.metadata?.gltf?.extras || {};
  // Fall back to mesh extras if needed
  const meshExtras = mesh.metadata?.gltf?.extras || mesh.metadata || {};
  const extras = { ...meshExtras, ...matExtras };
  
  return {
    windForce: extras.hm_materialWater_windForce ?? -5.0,
    waveHeight: extras.hm_materialWater_waveHeight ?? 0.1,
    bumpHeight: extras.hm_materialWater_bumpHeight ?? 0.1,
    windDirectionX: extras.hm_materialWater_windDirectionX ?? 1.0,
    windDirectionY: extras.hm_materialWater_windDirectionY ?? 1.0,
    waterColorR: extras.hm_materialWater_waterColorR ?? 0.1,
    waterColorG: extras.hm_materialWater_waterColorG ?? 0.1,
    waterColorB: extras.hm_materialWater_waterColorB ?? 0.6,
    colorBlendFactor: extras.hm_materialWater_colorBlendFactor ?? 0.0,
    waveLength: extras.hm_materialWater_waveLength ?? 0.1,
  };
}

/**
 * Create a water material and apply it to the mesh
 */
function createWaterMaterial(
  mesh: AbstractMesh,
  params: WaterMaterialParams,
  scene: Scene
): WaterMaterial {
  const materialName = `waterMaterial_${mesh.name}`;
  
  // Create water material
  const waterMaterial = new WaterMaterial(materialName, scene);
  
  // Set up bump texture (required for proper wave effect)
  // Using a default water bump map - can be customized via asset path
  waterMaterial.bumpTexture = new Texture(
    '/assets/textures/waterbump.png',
    scene
  );
  
  // Configure water properties from params
  waterMaterial.windForce = params.windForce;
  waterMaterial.waveHeight = params.waveHeight;
  waterMaterial.bumpHeight = params.bumpHeight;
  waterMaterial.windDirection = new Vector2(params.windDirectionX, params.windDirectionY);
  waterMaterial.waterColor = new Color3(params.waterColorR, params.waterColorG, params.waterColorB);
  waterMaterial.colorBlendFactor = params.colorBlendFactor;
  waterMaterial.waveLength = params.waveLength;
  
  // Disable backface culling so water is visible regardless of plane normal direction
  waterMaterial.backFaceCulling = false;
  
  // Add meshes to render list for reflections/refractions
  // Exclude the water mesh itself and any child meshes to prevent feedback loop
  const waterMeshId = mesh.uniqueId;
  scene.meshes.forEach((sceneMesh) => {
    // Skip if it's the water mesh itself or a child of the water mesh
    if (sceneMesh.uniqueId === waterMeshId || sceneMesh.parent?.uniqueId === waterMeshId) {
      return;
    }
    // Skip debug meshes - they shouldn't appear in reflections
    if (isDebugMesh(sceneMesh)) {
      return;
    }
    // Add all non-debug meshes to render list (regardless of visibility state)
    waterMaterial.addToRenderList(sceneMesh);
  });
  
  return waterMaterial;
}

/**
 * Check if a mesh is a debug visualization mesh that should be excluded from effects
 */
function isDebugMesh(mesh: AbstractMesh): boolean {
  const name = mesh.name;
  return (
    name.startsWith('debug') ||
    name.startsWith('angleArc') ||
    name.startsWith('currentSegment') ||
    name.includes('_debug_') ||
    name.startsWith('gizmo')
  );
}

export const waterMaterialBehavior: EntityBehavior = {
  id: 'material:water',
  displayName: 'Water Material',
  type: EntityType.DYNAMIC_OBJECT,
  category: 'object',
  tags: ['material', 'water', 'reflection', 'waves'],
  
  onSpawn: (api) => {
    let mesh = api.self.mesh;
    if (!mesh) {
      logger.assets.warn(`water_material behavior: No mesh for entity ${api.self.id}`);
      return;
    }
    
    // Check if this mesh has minimal geometry and prefer child with actual geometry
    const meshVertCount = mesh.getTotalVertices();
    if (meshVertCount <= 4) {
      const children = mesh.getChildMeshes();
      for (const child of children) {
        const childVerts = child.getTotalVertices();
        if (childVerts > meshVertCount && child.material) {
          logger.assets.debug(`water_material: Using child mesh ${child.name} instead of ${mesh.name}`);
          mesh = child;
          break;
        }
      }
    }
    
    const params = extractWaterParams(mesh);
    const scene = mesh.getScene();
    const waterMeshId = mesh.uniqueId; // Store for exclusion checks
    
    logger.assets.debug(`water_material: Creating water material for ${mesh.name}`, params);
    
    // Ensure mesh has tangent data required for bump mapping
    ensureTangents(mesh);
    
    // Create and apply water material
    const waterMaterial = createWaterMaterial(mesh, params, scene);
    mesh.material = waterMaterial;
    
    // Note: The water mesh itself is already excluded in createWaterMaterial()
    // Don't touch reflectionTexture.renderList directly - it's managed internally
    // by WaterMaterial and may not exist until first render
    
    // Observe scene for new meshes to add to render list
    const meshAddedObserver = scene.onNewMeshAddedObservable.add((newMesh) => {
      // Skip if it's the water mesh itself or a child of the water mesh
      if (newMesh.uniqueId === waterMeshId || newMesh.parent?.uniqueId === waterMeshId) {
        return;
      }
      // Skip debug meshes - they shouldn't appear in reflections
      if (isDebugMesh(newMesh)) {
        return;
      }
      // Add non-debug meshes to render list (WaterMaterial handles internal lists)
      waterMaterial.addToRenderList(newMesh);
    });
    
    // Store for cleanup and live editing
    waterMaterialsByEntity.set(api.self.id, {
      material: waterMaterial,
      mesh,
      params,
    });
    observersByEntity.set(api.self.id, meshAddedObserver);
    
    // Register with material behavior registry for UI
    registerMaterialBehavior({
      entityId: api.self.id,
      mesh,
      behaviorId: 'material:water',
      displayName: 'Water Material',
      getParams: (): Record<string, number | string | boolean> => {
        const entry = waterMaterialsByEntity.get(api.self.id);
        if (!entry) return {};
        const m = entry.material;
        return {
          windForce: m.windForce,
          waveHeight: m.waveHeight,
          bumpHeight: m.bumpHeight,
          windDirectionX: m.windDirection.x,
          windDirectionY: m.windDirection.y,
          waterColorR: m.waterColor.r,
          waterColorG: m.waterColor.g,
          waterColorB: m.waterColor.b,
          colorBlendFactor: m.colorBlendFactor,
          waveLength: m.waveLength,
        };
      },
      setParam: (name: string, value: number | string | boolean): void => {
        const entry = waterMaterialsByEntity.get(api.self.id);
        if (!entry) return;
        const m = entry.material;
        
        if (typeof value === 'number') {
          switch (name) {
            case 'windForce': m.windForce = value; break;
            case 'waveHeight': m.waveHeight = value; break;
            case 'bumpHeight': m.bumpHeight = value; break;
            case 'windDirectionX': m.windDirection.x = value; break;
            case 'windDirectionY': m.windDirection.y = value; break;
            case 'waterColorR': m.waterColor.r = value; break;
            case 'waterColorG': m.waterColor.g = value; break;
            case 'waterColorB': m.waterColor.b = value; break;
            case 'colorBlendFactor': m.colorBlendFactor = value; break;
            case 'waveLength': m.waveLength = value; break;
          }
        }
      },
    });
    
    // Return live editing interface
    return {
      getParams: (): Record<string, number | string | boolean> => {
        const entry = waterMaterialsByEntity.get(api.self.id);
        if (!entry) return {};
        const m = entry.material;
        return {
          windForce: m.windForce,
          waveHeight: m.waveHeight,
          bumpHeight: m.bumpHeight,
          windDirectionX: m.windDirection.x,
          windDirectionY: m.windDirection.y,
          waterColorR: m.waterColor.r,
          waterColorG: m.waterColor.g,
          waterColorB: m.waterColor.b,
          colorBlendFactor: m.colorBlendFactor,
          waveLength: m.waveLength,
        };
      },
      setParam: (name: string, value: number) => {
        const entry = waterMaterialsByEntity.get(api.self.id);
        if (!entry) return;
        const m = entry.material;
        const v = Number(value);
        switch (name) {
          case 'windForce': m.windForce = v; break;
          case 'waveHeight': m.waveHeight = v; break;
          case 'bumpHeight': m.bumpHeight = v; break;
          case 'windDirectionX': m.windDirection.x = v; break;
          case 'windDirectionY': m.windDirection.y = v; break;
          case 'waterColorR': m.waterColor.r = v; break;
          case 'waterColorG': m.waterColor.g = v; break;
          case 'waterColorB': m.waterColor.b = v; break;
          case 'colorBlendFactor': m.colorBlendFactor = v; break;
          case 'waveLength': m.waveLength = v; break;
        }
      },
    };
  },
  
  onDestroy: (api) => {
    unregisterMaterialBehavior(api.self.id);
    
    const entry = waterMaterialsByEntity.get(api.self.id);
    if (entry) {
      entry.material.dispose();
      waterMaterialsByEntity.delete(api.self.id);
    }
    
    // Remove scene observer
    const observer = observersByEntity.get(api.self.id);
    if (entry?.mesh && observer) {
      const scene = entry.mesh.getScene();
      scene.onNewMeshAddedObservable.remove(observer);
      observersByEntity.delete(api.self.id);
    }
    
    logger.assets.debug(`water_material: Disposed water material for ${api.self.id}`);
  },
};
